import { localDate } from '@/platform/lib/utils';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Badge,
    Divider,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    MenuSeparator,
    Segment,
    SegmentContent,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableRow,
} from '@/platform/ui';
import {
    EllipsisVerticalIcon,
    PencilIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { router, usePage } from '@inertiajs/react';
import EmploymentPeriods from './EmploymentPeriods';

const LkvEntitlementCard = ({ entitlement, onEdit }) => {
    const { auth, employee } = usePage().props;
    const { confirm, setLoading } = useConfirmationStore();
    const canManage = auth?.permissions?.includes('view lkv');
    const employments = entitlement.overlapping_employments ?? [];

    const handleDelete = () =>
        confirm({
            title: 'LKV-recht verwijderen?',
            description:
                'Het recht wordt verwijderd, samen met de getekende machtiging en de doelgroepverklaring. Dit kun je niet ongedaan maken.',
            confirmLabel: 'Verwijderen',
            onConfirm: close => {
                setLoading(true);

                router.delete(
                    `/customers/${employee.customer_id}/employees/${employee.id}/lkv/${entitlement.id}`,
                    { preserveScroll: true, onFinish: close }
                );
            },
        });

    return (
        <Segment>
            <SegmentHeader
                header={
                    <span className='inline-flex flex-wrap items-center gap-2'>
                        LKV
                        <Badge color={entitlement.category_color}>
                            {entitlement.category_label}
                        </Badge>
                        <Badge color={entitlement.state_color}>
                            {entitlement.state_label}
                        </Badge>
                    </span>
                }
            >
                {canManage && (
                    <Menu>
                        <MenuButton aria-label='Acties'>
                            <EllipsisVerticalIcon className='size-5' />
                        </MenuButton>
                        <MenuItems anchor='bottom end'>
                            <MenuItem
                                icon={PencilIcon}
                                onClick={() => onEdit(entitlement)}
                            >
                                Wijzigen
                            </MenuItem>
                            <MenuSeparator />
                            <MenuItem
                                className='text-red-600'
                                icon={TrashIcon}
                                onClick={handleDelete}
                            >
                                Verwijderen
                            </MenuItem>
                        </MenuItems>
                    </Menu>
                )}
            </SegmentHeader>
            <SegmentContent>
                <div className='flex flex-col gap-4'>
                    <Table striped>
                        <TableBody>
                            <TableRow>
                                <TableCell className='font-bold'>
                                    Startdatum
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {localDate(entitlement.start_date)}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className='font-bold'>
                                    Einddatum
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {entitlement.end_date
                                        ? localDate(entitlement.end_date)
                                        : 'Structureel'}
                                </TableCell>
                            </TableRow>
                            {entitlement.out_of_retention_at && (
                                <TableRow error>
                                    <TableCell className='font-bold'>
                                        Bewaartermijn tot
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {localDate(
                                            entitlement.out_of_retention_at
                                        )}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>

                    {employments.length === 0 ? (
                        <p className='text-sm text-gray-500'>
                            Er zijn geen dienstverbanden binnen de looptijd van
                            dit LKV-recht.
                        </p>
                    ) : (
                        <>
                            <Divider content='Dienstverbanden' />
                            <div className='flex flex-col gap-3'>
                                {employments.map(employment => (
                                    <EmploymentPeriods
                                        employment={employment}
                                        periods={employment.period_data}
                                        hoursKey='paid_hours'
                                        hoursLabel='Verloonde uren'
                                        key={employment.id}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </SegmentContent>
        </Segment>
    );
};

export default LkvEntitlementCard;
